import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import Footer from '../components/Footer';
import { ArrowRight, ExternalLink, Sparkles, Rocket, Loader2, CircleAlert } from 'lucide-react';
import { API_URL } from '../lib/apiBase';

const STATUS_META = {
  planned:     { label: 'Planned',     className: 'bg-blue-500/10 text-blue-600 border-blue-500/30' },
  in_progress: { label: 'In Progress', className: 'bg-amber-500/10 text-amber-600 border-amber-500/30' },
  shipped:     { label: 'Shipped',     className: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30' },
};

const PublicRoadmapPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/api/public/roadmap/${slug}`);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Roadmap not available');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="container mx-auto max-w-4xl space-y-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-6 w-96" />
          <div className="grid md:grid-cols-3 gap-4 mt-8">
            {[1,2,3].map(i => <Skeleton key={i} className="h-64 w-full" />)}
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 text-center" data-testid="public-roadmap-error">
        <CircleAlert className="h-10 w-10 text-muted-foreground mb-3" />
        <h1 className="text-2xl font-bold mb-2">Roadmap unavailable</h1>
        <p className="text-muted-foreground mb-6 max-w-md">{error || "This roadmap may be private or the URL is incorrect."}</p>
        <Button onClick={() => navigate('/')}>Back to VibeSprint</Button>
      </div>
    );
  }

  const { workspace, roadmap, total } = data;
  const columns = [
    { key: 'planned', items: roadmap.planned || [] },
    { key: 'in_progress', items: roadmap.in_progress || [] },
    { key: 'shipped', items: roadmap.shipped || [] },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid={`public-roadmap-${slug}`}>
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg flex items-center justify-center text-xl"
                 style={{ backgroundColor: `${workspace.color || '#8B5CF6'}20` }}>
              {workspace.icon || '📦'}
            </div>
            <div>
              <h1 className="text-xl font-bold">{workspace.name}</h1>
              <p className="text-xs text-muted-foreground">Public roadmap · {total} items</p>
            </div>
          </div>
          <a href="/" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
            <Sparkles className="h-3 w-3" /> Powered by VibeSprint
          </a>
        </div>
      </header>

      <main className="container mx-auto px-4 py-10 max-w-6xl">
        <div className="grid md:grid-cols-3 gap-4">
          {columns.map((col) => {
            const meta = STATUS_META[col.key];
            return (
              <div key={col.key} data-testid={`public-roadmap-column-${col.key}`}>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-semibold text-base">{meta.label}</h2>
                  <Badge variant="outline" className={meta.className}>{col.items.length}</Badge>
                </div>
                <div className="space-y-2">
                  {col.items.length === 0 ? (
                    <Card className="p-4 border-dashed text-sm text-muted-foreground text-center">
                      Nothing here yet.
                    </Card>
                  ) : col.items.map((it) => (
                    <Card key={it.signal_id} className="p-4" data-testid={`public-roadmap-item-${it.signal_id}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className="text-sm font-medium">{it.summary}</p>
                      </div>
                      <div className="flex flex-wrap gap-1.5 text-[10px]">
                        {it.decision_lane && (
                          <Badge variant="outline" className="capitalize">
                            {it.decision_lane.replace('_', ' ')}
                          </Badge>
                        )}
                        {it.signal?.channel && (
                          <Badge variant="secondary" className="capitalize">{it.signal.channel}</Badge>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {/* Footer CTA */}
      <section className="py-12 border-t bg-muted/20">
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <Rocket className="h-8 w-8 text-primary mx-auto mb-3" />
          <h2 className="text-2xl font-bold mb-2">Want a roadmap like this?</h2>
          <p className="text-muted-foreground mb-4">
            VibeSprint turns your feedback into a prioritized roadmap — automatically.
          </p>
          <Button onClick={() => navigate('/')} className="btn-cta" data-testid="public-roadmap-cta">
            Try VibeSprint
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </section>

      <Footer variant="minimal" />
    </div>
  );
};

export default PublicRoadmapPage;
